/**
 * LotSmith — Pricing page.
 *
 * Tiered pricing cards. Pro / Trader CTAs hit worker-billing's
 * /v1/checkout/session — if the user is signed in we redirect straight
 * to the Stripe-hosted Checkout URL; otherwise we punt to /login with
 * an `intent=tier` query the post-login flow can read to resume the
 * checkout. Free + Team don't route through Stripe.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

interface Tier {
  id: string;
  name: string;
  price: string;
  priceSuffix?: string;
  yearly?: string;
  tagline: string;
  features: string[];
  cta: string;
  href: string;
  featured?: boolean;
  /** When set, clicking the CTA hits worker-billing instead of
   *  navigating to `href`. */
  checkout?: { tier: "PRO" | "TRADER"; cadence: "monthly" | "annual" };
}

const TIERS: readonly Tier[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    tagline: "Local EA. No cloud sync.",
    features: [
      "1 broker account",
      "Position sizer + risk guard",
      "Trade manager (BE / trail / partial)",
      "Local daily-loss + cooldown enforcer",
      "30 days of dashboard history (manual)",
    ],
    cta: "Get the free EA",
    href: "/onboarding",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$12",
    priceSuffix: "/mo",
    yearly: "or $99/year",
    tagline: "Cloud sync + alerts.",
    features: [
      "5 broker accounts",
      "All Free EA features",
      "Cloud sync — trades + equity push",
      "Web dashboard + 1 year history",
      "Prop firm rule engine (FTMO, FundedNext, MFF, 5%ers, TopStep, E8)",
      "Telegram + Discord alerts",
      "Calendar P&L heatmap",
      "Trade journal notes",
      "Weekly review email",
      "Standard support",
    ],
    cta: "Start Pro",
    href: "/login?intent=pro_monthly",
    featured: true,
    checkout: { tier: "PRO", cadence: "monthly" },
  },
  {
    id: "trader",
    name: "Trader",
    price: "$29",
    priceSuffix: "/mo",
    yearly: "or $249/year",
    tagline: "Advanced discipline analytics.",
    features: [
      "20 broker accounts",
      "All Pro features",
      "Heuristic pattern detection",
      "Deeper time-of-day and symbol-bias review",
      "AI weekly review beta when enabled",
      "Discord delivery included",
      "Unlimited history",
      "Priority support (12h response)",
    ],
    cta: "Start Trader",
    href: "/login?intent=trader_monthly",
    checkout: { tier: "TRADER", cadence: "monthly" },
  },
  {
    id: "team",
    name: "Team",
    price: "$99",
    priceSuffix: "/mo",
    tagline: "Educators, signal providers, small prop firms.",
    features: [
      "Unlimited broker accounts",
      "All Trader features",
      "Multi-trader management dashboard",
      "API access (read + write)",
      "White-label option",
      "Bulk seat licensing",
      "99.5% uptime SLA",
    ],
    cta: "Talk to us",
    href: "/onboarding?contact=team",
  },
];

function billingApiBase(): string {
  return (import.meta.env.VITE_BILLING_API_BASE as string | undefined) ?? "";
}

export function Pricing() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout(tier: Tier): Promise<void> {
    if (!tier.checkout) return;
    const base = billingApiBase();
    if (!base) {
      // Worker not configured (local dev or before billing deploy) —
      // fall back to /login with the intent so the prod build doesn't
      // dead-end the user.
      window.location.href = tier.href;
      return;
    }
    setBusy(tier.id);
    setError(null);
    try {
      const r = await fetch(`${base}/v1/checkout/session`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(tier.checkout),
      });
      if (r.status === 401) {
        // Not signed in — punt to login, preserve intent.
        window.location.href = `/login?intent=${tier.checkout.tier.toLowerCase()}_${tier.checkout.cadence}`;
        return;
      }
      if (!r.ok) throw new Error(`checkout ${r.status}`);
      const j = await r.json() as { url: string };
      window.location.href = j.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't start checkout.");
      setBusy(null);
    }
  }

  return (
    <div className="pricing">
      <header className="nav">
        <div className="nav__inner">
          <Link to="/" className="nav__brand">LotSmith</Link>
          <nav className="nav__links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login" className="btn btn--ghost">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="pricing__hero">
        <div className="pricing__inner">
          <h1>Simple pricing</h1>
          <p className="pricing__lead">
            Free EA forever. Pro adds cloud sync, Telegram, Discord, rule
            telemetry, and weekly reviews. Trader is for deeper analytics as
            the dataset grows. Cancel anytime — 14-day refund.
          </p>
          <div className="pricing__switch-note">Monthly pricing shown. Annual prices are discounted for pre-launch users.</div>
        </div>
      </section>

      {error && (
        <div className="pricing__inner" style={{ marginBottom: 16 }}>
          <div className="form-error">{error}</div>
        </div>
      )}

      <section className="pricing__grid">
        {TIERS.map((t) => {
          const btnClass = `btn btn--block ${t.featured ? "btn--primary" : "btn--ghost"}`;
          const isBusy = busy === t.id;
          return (
            <article
              key={t.id}
              className={`pricing__card${t.featured ? " is-featured" : ""}`}
            >
              {t.featured && <div className="pricing__badge">Most popular</div>}
              <div className="pricing__name">{t.name}</div>
              <div className="pricing__price">
                <span className="pricing__amount">{t.price}</span>
                {t.priceSuffix && <span className="pricing__suffix">{t.priceSuffix}</span>}
              </div>
              {t.yearly && <div className="pricing__yearly">{t.yearly}</div>}
              <p className="pricing__tagline">{t.tagline}</p>
              <ul className="pricing__features">
                {t.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {t.checkout ? (
                <button
                  type="button"
                  className={btnClass}
                  onClick={() => void startCheckout(t)}
                  disabled={isBusy || busy !== null}
                >
                  {isBusy ? "Redirecting…" : t.cta}
                </button>
              ) : (
                <Link to={t.href} className={btnClass}>{t.cta}</Link>
              )}
            </article>
          );
        })}
      </section>

      <section className="pricing__included">
        <div className="pricing__inner pricing__included-grid">
          <div>
            <h2>What is live today</h2>
            <p>
              MT5 EA, cloud sync, dashboard metrics, calendar, journal, Telegram,
              Discord webhook, weekly email, billing portal, and discipline insights.
            </p>
          </div>
          <div>
            <h2>What stays honest</h2>
            <p>
              AI review and percentile scoring are marked as beta/deferred until
              there is enough real cohort data. The free EA remains usable without
              subscription or cloud access.
            </p>
          </div>
        </div>
      </section>

      <section className="pricing__faq">
        <div className="pricing__inner">
          <h2>FAQ</h2>
          <dl>
            <dt>Do prop firms allow LotSmith?</dt>
            <dd>
              Most do — we don't auto-trade. You click BUY / SELL yourself; the
              EA only sizes, tracks rules, and pushes data. Verify with your
              firm's TOS before installing on a funded account. We have an
              email template you can copy in our docs.
            </dd>
            <dt>What if my MT5 disconnects?</dt>
            <dd>
              The EA queues events locally and flushes when the network is
              back. Telegram alerts and dashboard updates are best-effort —
              read the disclaimer.
            </dd>
            <dt>Can I cancel anytime?</dt>
            <dd>
              Yes. Cancel in <code>/app/settings → Subscription</code>. You
              keep access until the end of the current period. 14-day no-
              questions refund on first payment.
            </dd>
            <dt>Do you support MT4?</dt>
            <dd>
              Not yet. We're MT5-only at launch. ~70% of prop traders we
              talked to use MT5; we'll add MT4 once we hit 50+ paid users.
            </dd>
          </dl>
        </div>
      </section>

      <footer className="pricing__foot">
        <div className="pricing__inner">
          <span>© 2026 LotSmith.</span>
          <span className="pricing__footlinks">
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href="/refund">Refund</a>
            <a href="/disclaimer">Disclaimer</a>
            <a href="/status">Status</a>
            <a href="mailto:support@lotsmith.app">Support</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
